// =====================================================================
// Transfer Windows page — inline SVG icons.
//
// Two flavours below:
//   - KPI icons (CheckIcon / ClockIcon / AlertIcon / CalendarIcon) are
//     pre-rendered 32x32 JSX elements with the colored card background
//     built into the SVG itself. Use them as `{CheckIcon}`, NOT
//     `<CheckIcon />` — they are React elements, not components.
//   - Line icons (SearchIcon / ChevronIcon / EyeIcon / etc.) are tiny
//     function components rendered via the shared <Icon> wrapper. Use
//     them as `<SearchIcon />`.
// =====================================================================
import React from "react";

const Icon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  />
);

// ── KPI icons (32x32 with built-in background) ─────────────────────────
export const CheckIcon = (
  <svg
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M0 10C0 4.47715 4.47715 0 10 0H22C27.5228 0 32 4.47715 32 10V22C32 27.5228 27.5228 32 22 32H10C4.47715 32 0 27.5228 0 22V10Z"
      fill="#F0FDF4"
    />
    <g clip-path="url(#clip0_6_5805)">
      <path
        d="M15.9999 22.6667C19.6818 22.6667 22.6666 19.6819 22.6666 16C22.6666 12.3181 19.6818 9.33334 15.9999 9.33334C12.318 9.33334 9.33325 12.3181 9.33325 16C9.33325 19.6819 12.318 22.6667 15.9999 22.6667Z"
        stroke="#00A63E"
        stroke-width="1.33333"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M14 16L15.3333 17.3333L18 14.6667"
        stroke="#00A63E"
        stroke-width="1.33333"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </g>
    <defs>
      <clipPath id="clip0_6_5805">
        <rect width="16" height="16" fill="white" transform="translate(8 8)" />
      </clipPath>
    </defs>
  </svg>
);

export const ClockIcon = (
  <svg
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M0 10C0 4.47715 4.47715 0 10 0H22C27.5228 0 32 4.47715 32 10V22C32 27.5228 27.5228 32 22 32H10C4.47715 32 0 27.5228 0 22V10Z"
      fill="#FFF7ED"
    />
    <g clip-path="url(#clip0_6_5800)">
      <path
        d="M15.9999 22.6667C19.6818 22.6667 22.6666 19.6819 22.6666 16C22.6666 12.3181 19.6818 9.33334 15.9999 9.33334C12.318 9.33334 9.33325 12.3181 9.33325 16C9.33325 19.6819 12.318 22.6667 15.9999 22.6667Z"
        stroke="#F54900"
        stroke-width="1.33333"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M16 12V16L18.6667 17.3333"
        stroke="#F54900"
        stroke-width="1.33333"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </g>
    <defs>
      <clipPath id="clip0_6_5800">
        <rect width="16" height="16" fill="white" transform="translate(8 8)" />
      </clipPath>
    </defs>
  </svg>
);

export const AlertIcon = (
  <svg
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M0 10C0 4.47715 4.47715 0 10 0H22C27.5228 0 32 4.47715 32 10V22C32 27.5228 27.5228 32 22 32H10C4.47715 32 0 27.5228 0 22V10Z"
      fill="#FDF3F4"
    />
    <g clip-path="url(#clip0_6_5878)">
      <path
        d="M15.9999 22.6667C19.6818 22.6667 22.6666 19.6819 22.6666 16C22.6666 12.3181 19.6818 9.33334 15.9999 9.33334C12.318 9.33334 9.33325 12.3181 9.33325 16C9.33325 19.6819 12.318 22.6667 15.9999 22.6667Z"
        stroke="#D0162D"
        stroke-width="1.33333"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M16 13.3333V16"
        stroke="#D0162D"
        stroke-width="1.33333"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M16 18.6667H16.0067"
        stroke="#D0162D"
        stroke-width="1.33333"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </g>
    <defs>
      <clipPath id="clip0_6_5878">
        <rect width="16" height="16" fill="white" transform="translate(8 8)" />
      </clipPath>
    </defs>
  </svg>
);

export const CalendarIcon = (
  <svg
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M0 10C0 4.47715 4.47715 0 10 0H22C27.5228 0 32 4.47715 32 10V22C32 27.5228 27.5228 32 22 32H10C4.47715 32 0 27.5228 0 22V10Z"
      fill="#EFF6FF"
    />
    <path
      d="M13.3333 9.33334V12"
      stroke="#155DFC"
      stroke-width="1.33333"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path
      d="M18.6667 9.33334V12"
      stroke="#155DFC"
      stroke-width="1.33333"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path
      d="M20.6667 10.6667H11.3333C10.597 10.6667 10 11.2636 10 12V21.3333C10 22.0697 10.597 22.6667 11.3333 22.6667H20.6667C21.403 22.6667 22 22.0697 22 21.3333V12C22 11.2636 21.403 10.6667 20.6667 10.6667Z"
      stroke="#155DFC"
      stroke-width="1.33333"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path
      d="M10 14.6667H22"
      stroke="#155DFC"
      stroke-width="1.33333"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
);

// ── Line icons (function components) ───────────────────────────────────
export const SearchIcon = (p) => (
  <Icon width="16" height="16" {...p}>
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-3.5-3.5" />
  </Icon>
);
export const ChevronIcon = (p) => (
  <Icon width="14" height="14" {...p}>
    <path d="M6 9l6 6 6-6" />
  </Icon>
);
export const EyeIcon = (p) => (
  <Icon width="14" height="14" {...p}>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
    <circle cx="12" cy="12" r="3" />
  </Icon>
);
export const FilterIcon = (p) => (
  <Icon width="14" height="14" {...p}>
    <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
  </Icon>
);
export const SunIcon = (p) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clip-path="url(#clip0_6_6293)">
      <path
        d="M7.99992 10.6667C9.47268 10.6667 10.6666 9.47275 10.6666 7.99999C10.6666 6.52724 9.47268 5.33333 7.99992 5.33333C6.52716 5.33333 5.33325 6.52724 5.33325 7.99999C5.33325 9.47275 6.52716 10.6667 7.99992 10.6667Z"
        stroke="#FF6900"
        stroke-width="1.33333"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M8 1.33333V2.66666"
        stroke="#FF6900"
        stroke-width="1.33333"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M8 13.3333V14.6667"
        stroke="#FF6900"
        stroke-width="1.33333"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M3.28662 3.28667L4.22662 4.22667"
        stroke="#FF6900"
        stroke-width="1.33333"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M11.7734 11.7733L12.7134 12.7133"
        stroke="#FF6900"
        stroke-width="1.33333"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M1.33325 8H2.66659"
        stroke="#FF6900"
        stroke-width="1.33333"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M13.3333 8H14.6666"
        stroke="#FF6900"
        stroke-width="1.33333"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M4.22662 11.7733L3.28662 12.7133"
        stroke="#FF6900"
        stroke-width="1.33333"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M12.7134 3.28667L11.7734 4.22667"
        stroke="#FF6900"
        stroke-width="1.33333"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </g>
    <defs>
      <clipPath id="clip0_6_6293">
        <rect width="16" height="16" fill="white" />
      </clipPath>
    </defs>
  </svg>
);
export const SnowIcon = (p) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6.66667 13.3333L5.83333 11.6667L4 12"
      stroke="#2B7FFF"
      stroke-width="1.33333"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path
      d="M6.66667 2.66667L5.83333 4.33334L4 4.00001"
      stroke="#2B7FFF"
      stroke-width="1.33333"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path
      d="M9.33325 13.3333L10.1666 11.6667L11.9999 12"
      stroke="#2B7FFF"
      stroke-width="1.33333"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path
      d="M9.33325 2.66667L10.1666 4.33334L11.9999 4.00001"
      stroke="#2B7FFF"
      stroke-width="1.33333"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path
      d="M11.3334 14L9.33341 10H6.66675"
      stroke="#2B7FFF"
      stroke-width="1.33333"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path
      d="M11.3333 2L9.33325 6L10.3333 8"
      stroke="#2B7FFF"
      stroke-width="1.33333"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path
      d="M1.33325 8H5.66659L6.66659 6"
      stroke="#2B7FFF"
      stroke-width="1.33333"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path
      d="M13.3333 6.66667L12.3333 8.00001L13.3333 9.33334"
      stroke="#2B7FFF"
      stroke-width="1.33333"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path
      d="M14.6666 8H10.3333L9.33325 10"
      stroke="#2B7FFF"
      stroke-width="1.33333"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path
      d="M2.66675 6.66667L3.66675 8.00001L2.66675 9.33334"
      stroke="#2B7FFF"
      stroke-width="1.33333"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path
      d="M4.66675 14L6.66675 10L5.66675 8"
      stroke="#2B7FFF"
      stroke-width="1.33333"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path
      d="M4.66675 2L6.66675 6H9.33341"
      stroke="#2B7FFF"
      stroke-width="1.33333"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
);

export const BuildingIcon = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clip-path="url(#clip0_6_6338)">
      <path
        d="M4 14.6667V2.66666C4 2.31304 4.14048 1.9739 4.39052 1.72385C4.64057 1.4738 4.97971 1.33333 5.33333 1.33333H10.6667C11.0203 1.33333 11.3594 1.4738 11.6095 1.72385C11.8595 1.9739 12 2.31304 12 2.66666V14.6667H4Z"
        stroke="#155DFC"
        stroke-width="1.33333"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M3.99992 8H2.66659C2.31296 8 1.97382 8.14048 1.72378 8.39052C1.47373 8.64057 1.33325 8.97971 1.33325 9.33333V13.3333C1.33325 13.687 1.47373 14.0261 1.72378 14.2761C1.97382 14.5262 2.31296 14.6667 2.66659 14.6667H3.99992"
        stroke="#155DFC"
        stroke-width="1.33333"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M12 6H13.3333C13.687 6 14.0261 6.14048 14.2761 6.39052C14.5262 6.64057 14.6667 6.97971 14.6667 7.33333V13.3333C14.6667 13.687 14.5262 14.0261 14.2761 14.2761C14.0261 14.5262 13.687 14.6667 13.3333 14.6667H12"
        stroke="#155DFC"
        stroke-width="1.33333"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M6.66675 4H9.33341"
        stroke="#155DFC"
        stroke-width="1.33333"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M6.66675 6.66667H9.33341"
        stroke="#155DFC"
        stroke-width="1.33333"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M6.66675 9.33333H9.33341"
        stroke="#155DFC"
        stroke-width="1.33333"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M6.66675 12H9.33341"
        stroke="#155DFC"
        stroke-width="1.33333"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </g>
    <defs>
      <clipPath id="clip0_6_6338">
        <rect width="16" height="16" fill="white" />
      </clipPath>
    </defs>
  </svg>
);
